import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { Alert } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import { useAnalytics } from "@/providers/analytics";
import { AlertDialog, RobotoButton } from "@/shared/components/index.tsx";
import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { TagInput, Tags, TagsGroup } from "@/shared/components/tags";
import { UserLink } from "@/shared/components/UserLink";
import { CollectionRecord } from "@/shared/domain/collections";
import { useDeleteCollection } from "@/shared/domain/collections/hooks/useDeleteCollection";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";

import styles from "./CollectionDetailsTab.module.css";

interface CollectionDetailsTabProps {
  collection: CollectionRecord;
}

export const CollectionDetailsTab: React.FC<CollectionDetailsTabProps> = ({
  collection,
}) => {
  const { trackEvent } = useAnalytics();
  const { goto } = useNavigation();

  const { mutateAsync: updateCollection, error } = useUpdateCollection();
  const { mutateAsync: deleteCollection, error: deleteError } =
    useDeleteCollection();

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  async function handleAddTag(tag: string) {
    trackEvent("AddCollectionTagClicked", {
      collectionId: collection.collection_id,
    });
    await updateCollection({
      collectionId: collection.collection_id,
      resourceOwnerId: collection.org_id,
      updates: { add_tags: [tag] },
    });
  }

  async function handleRemoveTag(tag: string) {
    await updateCollection({
      collectionId: collection.collection_id,
      resourceOwnerId: collection.org_id,
      updates: { remove_tags: [tag] },
    });
  }

  return (
    <div className={styles.detailsGrid}>
      {(error ?? deleteError) && (
        <Alert severity="error" style={{ gridColumn: "1 / -1" }}>
          {(error ?? deleteError)!.message}
        </Alert>
      )}

      <span className={styles.detailLabel}>Created</span>
      <span className={styles.detailValue}>
        {new Date(collection.created).toLocaleString()} by{" "}
        <UserLink
          userId={collection.created_by}
          linkSx={{ fontSize: "var(--font-size-sm)" }}
        />
      </span>

      <span className={styles.detailLabel}>Updated</span>
      <span className={styles.detailValue}>
        {new Date(collection.updated).toLocaleString()} by{" "}
        <UserLink
          userId={collection.updated_by}
          linkSx={{ fontSize: "var(--font-size-sm)" }}
        />
      </span>

      <span className={styles.detailLabel}>Tags</span>
      <div className={styles.tagSection}>
        <TagInput
          autocompleteType={AutocompleteType.CollectionTags}
          onAddTag={(tag) => void handleAddTag(tag)}
          tags={collection.tags}
          size="small"
        />
        {collection.tags.length > 0 && (
          <TagsGroup>
            <Tags
              tags={collection.tags}
              onDeleteTag={(tag) => void handleRemoveTag(tag)}
            />
          </TagsGroup>
        )}
      </div>
      <span className={styles.detailLabel}>Operations</span>
      <div>
        <RobotoButton
          eventName="DeleteCollectionClicked"
          eventProperties={{ collectionId: collection.collection_id }}
          variant="outlined"
          size="small"
          color="error"
          startIcon={<DeleteOutlinedIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete Collection
        </RobotoButton>
      </div>

      <AlertDialog
        dialogOpen={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        dialogTitle="Delete Collection"
        dialogText={`Are you sure you want to delete ${collection.name ? `${collection.name} (${collection.collection_id})` : collection.collection_id}?`}
        dialogAction={async () => {
          await deleteCollection({
            collectionId: collection.collection_id,
            orgId: collection.org_id,
            resourceOwnerId: collection.org_id,
          });
          goto.collections();
        }}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};
